import { useRegisterSW } from 'virtual:pwa-register/react';
import { theme } from '../theme.js';

const CHECK_EVERY_MS = 30 * 60_000;

/**
 * Registers the service worker and, when a new version has been deployed,
 * asks before reloading — a silent reload could drop a note someone is
 * halfway through typing. Installed PWAs can stay open for days, so it also
 * checks for updates periodically.
 */
export function UpdateBanner() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => void registration.update(), CHECK_EVERY_MS);
    },
  });

  // The plugin only reloads on workbox's `controlling` event with isUpdate,
  // which never fires if another tab/window already activated the new worker
  // (or workbox counts it as "external") — the button then did nothing. So:
  // tell the waiting worker directly, reload on any controller change, and
  // reload anyway after a moment as a backstop.
  function reload() {
    let done = false;
    const go = () => { if (!done) { done = true; window.location.reload(); } };
    navigator.serviceWorker?.addEventListener('controllerchange', go);
    void navigator.serviceWorker?.getRegistration()
      .then((r) => r?.waiting?.postMessage({ type: 'SKIP_WAITING' }));
    void updateServiceWorker(true);
    setTimeout(go, 2000);
  }

  if (!needRefresh) return null;

  return (
    <div role="status" style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(76px + env(safe-area-inset-bottom))',
      zIndex: 50, maxWidth: 480, margin: '0 auto',
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', borderRadius: 12,
      background: theme.text, color: theme.bg,
      boxShadow: '0 6px 20px rgba(0,0,0,.18)', fontFamily: theme.font,
    }}>
      <span style={{ flex: 1, fontSize: 13 }}>A new version of MusterTap is available.</span>
      <button onClick={() => setNeedRefresh(false)} style={{ ...btn, background: 'transparent', color: theme.bg }}>
        Later
      </button>
      <button onClick={reload} style={{ ...btn, background: theme.accent, color: theme.accentInk }}>
        Reload
      </button>
    </div>
  );
}

const btn: React.CSSProperties = {
  border: 'none', borderRadius: 8, padding: '7px 12px',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
};
